<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <ModuleShell
    :title="$t('views.selectedGoat.modules.dag')"
    :icon="ListChecks"
    @click="goToDag"
    class="cursor-pointer"
  >
    <div v-if="dagStore.loading" class="text-sm opacity-50 text-center">
      {{ $t('views.selectedGoat.loading') }}
    </div>
     
    <!-- Display current dag if available -->
    <div v-else-if="latestDag" class="flex items-center justify-between">
      <div>
        <p class="text-sm opacity-70">
      {{ $t('views.selectedGoat.dag.current') }}
    </p>

        <div
          class="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl"
          :class="getScoreBgClass(latestDag.score)"
          :style="getScoreBgStyle(latestDag.score)"
        >
          {{ latestDag.score }}
        </div>

      </div>
      <div class="text-right">
        <p class="text-xs text-[var(--color-text)] opacity-50">
          {{ formatDate(latestDag.measuredAt) }}
        </p>
      </div>
    </div>

    <!-- No dag data -->
    <div v-else class="text-sm opacity-70 text-center py-2">
      {{ $t('views.selectedGoat.dag.noData') }}
    </div>
  </ModuleShell>
</template>


<script setup>
import { onMounted, computed } from 'vue'
import { useDagStore } from '@/stores/dag.store.js'
import { storeToRefs } from 'pinia'
import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
import { useRouter } from 'vue-router'
import { ListChecks } from 'lucide-vue-next'
import { formatDate } from '@/utils/format/dateformatting'

const props = defineProps({
  goatId: { type: Number, required: true }
})

const router = useRouter()
const dagStore = useDagStore()
const { dagChecks } = storeToRefs(dagStore)

// Get the latest dag (most recent)
const latestDag = computed(() => {
  if (!dagChecks.value || dagChecks.value.length === 0) return null
  return dagChecks.value[0]
})

function getScoreBgClass(score) {
  const classes = {
    1: 'border-2 border-gray-300 !text-gray-900',
    2: '!text-gray-900',
    3: '',
    4: '',
    5: ''
  }
  return classes[score] || ''
}

function getScoreBgStyle(score) {
  const styles = {
    1: 'background-color: #ffffff',
    2: 'background-color: #fedcca',
    3: 'background-color: #faa89e',
    4: 'background-color: #ff7963',
    5: 'background-color: #f94943'
  }
  return styles[score] || 'background-color: #6b7280'
}


function goToDag() {
  if (!props.goatId) return
  router.push({ name: 'Dag', params: { id: props.goatId } })
}

//function goToDag() {
//  router.push(`/goats/${props.goatId}/dag`)
//}

// Fetch dag if mounted
onMounted(() => {
  if (props.goatId) dagStore.fetchDagChecks(props.goatId)
})
</script>

<style scoped>
</style>